import React from "react";
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "@/admin/lib/AuthContext";
import ProtectedRoute from "@/admin/lib/ProtectedRoute";

import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/pages/Dashboard";
import Keys from "@/admin/pages/Keys";
import Playlists from "@/admin/pages/Playlists";
import MetadataAndSnapshot from "@/admin/pages/MetadataAndSnapshot";
import AppStoreReadiness from "@/admin/pages/AppStoreReadiness";
import Devices from "@/admin/pages/Devices";
import Branding from "@/admin/pages/Branding";
import Notifications from "@/admin/pages/Notifications";
import Updates from "@/admin/pages/Updates";
import DebugLogs from "@/admin/pages/DebugLogs";
import MissingMetadata from "@/admin/pages/MissingMetadata";
import ProfileCache from "@/admin/pages/ProfileCache";
import UsersPage from "@/admin/pages/Users";
import SettingsPage from "@/admin/pages/Settings";
import Login from "@/admin/pages/Login";
import ForgotPassword from "@/admin/pages/ForgotPassword";
import ResetPassword from "@/admin/pages/ResetPassword";

import Landing from "@/landing/Landing";
import Trial from "@/landing/Trial";
import ActivationBuy from "@/landing/Activation";
import PlaylistManager from "@/landing/Playlist";
import Privacy from "@/landing/Privacy";
import Terms from "@/landing/Terms";
import Support from "@/landing/Support";
import Refund from "@/landing/Refund";
import Clients from "@/admin/pages/Clients";
import Website from "@/admin/pages/Website";
import Paygate from "@/admin/pages/Paygate";
import PricePlans from "@/admin/pages/PricePlans";
import BuyCredits from "@/admin/pages/BuyCredits";
import PendingPayments from "@/admin/pages/PendingPayments";

import { DeviceProvider } from "@/player/lib/DeviceContext";
import PlayerRoot from "@/player/PlayerRoot";
import PlayerLayout from "@/player/components/PlayerLayout";
import Activation from "@/player/pages/Activation";
import Intro from "@/player/pages/Intro";
import ProfileSelect from "@/player/pages/ProfileSelect";
import MainMenu from "@/player/pages/MainMenu";
import Browse from "@/player/pages/Browse";
import CatchUp from "@/player/pages/CatchUp";
import TvGuide from "@/player/pages/TvGuide";
import LiveTvWatch from "@/player/pages/LiveTvWatch";
import Watch from "@/player/pages/Watch";
import Search from "@/player/pages/Search";
import PlaylistsSwitcher from "@/player/pages/PlaylistsSwitcher";
import NotificationsPanel from "@/player/pages/NotificationsPanel";
import PlayerSettings from "@/player/pages/PlayerSettings";
import HideCategories from "@/player/pages/HideCategories";
import DebugConsole from "@/player/components/DebugConsole";
import { installDebugLogShipper } from "@/player/lib/debugLogShipper";
import { installTvDetection } from "@/player/lib/tvDetect";
import { APP_VERSION } from "@/player/lib/version";

// The same React bundle is shipped to: the web (admin-only), the Electron
// desktop player, and the Capacitor Android player. We expose the player UI
// only when running inside one of the desktop / mobile wrappers — the web
// host (zaplayerpro.club) is admin-only.
const IS_ELECTRON = typeof window !== "undefined" && window.electronAPI?.isElectron;
const IS_CAPACITOR =
  typeof window !== "undefined" &&
  (window.Capacitor?.isNativePlatform?.() ||
    process.env.REACT_APP_PLATFORM === "android");
// v1.4.7 — Web Player mode. The `/webplayer` route sets a localStorage
// flag that unlocks the same player UI shipped with Android + Electron
// but running in a plain browser tab. It still requires an activation
// key (consumes a device slot) and is meant to be reached from the
// "Web Player" link in the admin sidebar.
const IS_WEBPLAYER =
  typeof window !== "undefined" &&
  (localStorage.getItem("zapp_webplayer_enabled") === "1" ||
    window.location.pathname.startsWith("/webplayer") ||
    window.location.pathname.startsWith("/admin/webplayer") ||
    // v1.3.2 — `app.zaplayerpro.club` (and any `app.*` subdomain) is
    // the dedicated web-player host. Recognising it here makes the
    // inline Live-TV preview + audio playback work by default; before
    // this, users landed on the host with `__ZAPP_IS_WEBPLAYER__ =
    // false` and the pane showed only a "Watch fullscreen" button.
    (window.location.hostname || "").startsWith("app."));
const IS_NATIVE = IS_ELECTRON || IS_CAPACITOR;
const CAN_USE_PLAYER = IS_NATIVE || IS_WEBPLAYER;

if (typeof window !== "undefined") {
  window.__ZAPP_IS_WEBPLAYER__ = IS_WEBPLAYER;

  // v1.4.17 — Clear a stale Web-Player opt-in flag when a plain
  // browser lands on a public marketing URL. Without this, a user
  // who once visited /webplayer or /admin/webplayer would be
  // permanently redirected from zaplayerpro.club/ to /player/activate
  // even after the marketing site relaunched. The flag is only
  // meaningful once we're actually rendering the player subtree,
  // so it's safe to drop here for everyone else.
  try {
    const p = window.location.pathname || "/";
    const isMarketingPath =
      p === "/" ||
      p.startsWith("/trial") ||
      p.startsWith("/activation") ||
      p.startsWith("/admin/login");
    if (!IS_NATIVE && isMarketingPath) {
      localStorage.removeItem("zapp_webplayer_enabled");
    }
  } catch { /* localStorage blocked */ }
}

// Stamp the running app version on `window` so the debug-log shipper
// can include it in every POST without taking a hard import dependency.
if (typeof window !== "undefined") {
  window.__ZAPP_VERSION__ = APP_VERSION;
  installDebugLogShipper();
  // Mark <html data-tv="true"> ONLY on the native player builds so global
  // CSS can dial down paddings / base font-size in one place. Skipped on
  // the admin panel (browser) because its layout is sized for desktops.
  if (IS_NATIVE) installTvDetection();

  // Admin-configured global User-Agent — fetched once on launch and
  // stashed on `window.__ZAPP_UA__` so every downstream module (native
  // player, fetch wrappers, debug shipper) can pick it up without
  // taking a hard dependency on the bootstrap module. Cached in
  // localStorage so the value survives offline launches. The fetch
  // is async and fire-and-forget — if it fails (no network on first
  // launch) the cached or default UA is used.
  try {
    const cached = localStorage.getItem("zapp_app_user_agent");
    if (cached) window.__ZAPP_UA__ = cached;
  } catch { /* localStorage blocked */ }
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  // v1.3.42 — Native (Android APK / Electron) builds always talk to
  // the live admin host, never the preview URL that was baked in at
  // yarn-build time. Otherwise a months-old APK fires this fetch at a
  // dead preview URL → 404. The rest of the player app routes through
  // playerApi.js → resolveBackendUrl() which does the same thing at
  // runtime; this bootstrap fetch was the lone outlier.
  const isCapacitor =
    typeof window !== "undefined" &&
    window.Capacitor?.isNativePlatform?.();
  const isElectron =
    typeof window !== "undefined" && window.electronAPI?.isElectron;
  const configBase = isCapacitor || isElectron ? "https://zaplayerpro.club" : apiBase;
  if (configBase) {
    fetch(`${configBase}/api/public/player-config`, { method: "GET" })
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        const ua = (cfg && cfg.app_user_agent) || "";
        if (ua) {
          window.__ZAPP_UA__ = ua;
          try { localStorage.setItem("zapp_app_user_agent", ua); } catch { /* ignore */ }
        } else {
          // Admin cleared the override → drop the cached value so
          // we revert to the engine default on next launch.
          window.__ZAPP_UA__ = null;
          try { localStorage.removeItem("zapp_app_user_agent"); } catch { /* ignore */ }
        }
      })
      .catch(() => { /* offline — keep cached value */ });
  }
}

/**
 * v1.4.7 — WebPlayerGate
 * Sets the localStorage flag that unlocks the /player routes in a plain
 * browser tab, then hard-navigates to /player so the top-level constants
 * re-evaluate with the flag already set.
 *
 * v1.4.77 — Switched from `window.location.replace` to React Router's
 * `useNavigate` so the transition stays inside the WKWebView on iOS
 * Capacitor. `window.location.*` triggers a full-page navigation which
 * iOS 14+ (with app-bound-domains enforced) can treat as external and
 * kick out to Safari, bypassing the app entirely. Client-side navigate
 * keeps everything in the SPA / WebView.
 */
function WebPlayerGate() {
  const navigate = ReactRouterNavigateHook();
  React.useEffect(() => {
    try {
      localStorage.setItem("zapp_webplayer_enabled", "1");
    } catch {/* ignore */}
    // Use client-side navigate; falls back to hard-nav for older
    // browsers where React Router isn't mounted (defensive only).
    if (typeof navigate === "function") {
      navigate("/player", { replace: true });
    } else {
      window.location.replace("/player");
    }
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white/60 text-sm">
      Opening Web Player…
    </div>
  );
}

// Isolated hook wrapper — imported lazily to avoid tripping the initial
// top-level `IS_WEBPLAYER` constant evaluation (which runs before the
// Router provider mounts).
function ReactRouterNavigateHook() {
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useNavigate();
  } catch {
    return null;
  }
}


import FocusDebugOverlay from "@/player/components/FocusDebugOverlay";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

function App() {
  // Electron uses HashRouter (file:// loads); web + Capacitor use BrowserRouter.
  const Router = IS_ELECTRON ? HashRouter : BrowserRouter;
  return (
    <div className="App">
      <FocusDebugOverlay />
      <PWAInstallPrompt />
      <Router>
        <AuthProvider>
          <DeviceProvider>
            <Routes>
              {/* Root: player on native wrappers ONLY. Browser visits
                  to zaplayerpro.club/ always land on the public
                  marketing site, even if a previous session had
                  opted into "Web Player" mode. Web-Player mode
                  remains reachable via the /webplayer route. */}
              <Route
                path="/"
                element={IS_NATIVE ? <Navigate to="/player" replace /> : <Landing />}
              />

              {/* Public trial page — anyone can claim a free 5-day activation. */}
              <Route path="/trial" element={<Trial />} />

              {/* Public activation / buy page. */}
              <Route path="/activation" element={<ActivationBuy />} />

              {/* Public client self-service playlist manager. Auth is by
                  activation key OR MAC address — no admin login needed. */}
              <Route path="/playlist" element={<PlaylistManager />} />

              {/* Legal / support pages — required by App Store and Play
                  Store submissions. Reviewers explicitly check that these
                  URLs return a 200 with real, human-written content. */}
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/support" element={<Support />} />

              {/* v1.4.7 — Web Player opt-in gate.
                  Visiting `/webplayer` OR `/admin/webplayer` sets a
                  localStorage flag and redirects to `/player`. From
                  then on the player UI is available in this browser
                  tab (activation key required, consumes a slot).
                  Reached from the "Web Player" link in the admin sidebar. */}
              <Route path="/webplayer" element={<WebPlayerGate />} />
              <Route path="/admin/webplayer" element={<WebPlayerGate />} />

              {/* ====== Player App (Electron + Android + opt-in Web) ====== */}
              <Route
                path="/player"
                element={CAN_USE_PLAYER ? <PlayerRoot /> : <Navigate to="/admin/login" replace />}
              >
                <Route index element={<Navigate to="menu" replace />} />
                <Route path="activate" element={<Activation />} />
                <Route path="intro" element={<Intro />} />
                <Route path="profiles" element={<ProfileSelect />} />
                <Route path="watch" element={<Watch />} />

                {/* Screens wrapped in the player chrome (top bar) */}
                <Route element={<PlayerLayout />}>
                  <Route path="menu" element={<MainMenu />} />
                  <Route path="browse/:type" element={<Browse />} />
                  <Route path="catchup" element={<CatchUp />} />
                  <Route path="guide" element={<TvGuide />} />
                  <Route path="live/:channelId" element={<LiveTvWatch />} />
                  <Route path="search" element={<Search />} />
                  <Route path="playlists" element={<PlaylistsSwitcher />} />
                  <Route path="notifications" element={<NotificationsPanel />} />
                  <Route path="settings" element={<PlayerSettings />} />
                  <Route path="settings/hide-categories" element={<HideCategories />} />
                </Route>
              </Route>

              {/* ====== Admin Panel (web + Electron) ====== */}
              <Route path="/admin/login" element={<Login />} />
              <Route path="/admin/forgot-password" element={<ForgotPassword />} />
              <Route path="/admin/reset-password" element={<ResetPassword />} />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="keys" element={<Keys />} />
                <Route path="playlists" element={<Playlists />} />
                <Route
                  path="metadata-snapshot"
                  element={
                    <ProtectedRoute adminOnly>
                      <MetadataAndSnapshot />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app-store-readiness"
                  element={
                    <ProtectedRoute adminOnly>
                      <AppStoreReadiness />
                    </ProtectedRoute>
                  }
                />
                <Route path="devices" element={<Devices />} />
                <Route path="clients" element={<Clients />} />
                <Route
                  path="website"
                  element={
                    <ProtectedRoute adminOnly>
                      <Website />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="paygate"
                  element={
                    <ProtectedRoute adminOnly>
                      <Paygate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="price-plans"
                  element={
                    <ProtectedRoute adminOnly>
                      <PricePlans />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="buy-credits"
                  element={
                    <ProtectedRoute>
                      <BuyCredits />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="pending-payments"
                  element={
                    <ProtectedRoute adminOnly>
                      <PendingPayments />
                    </ProtectedRoute>
                  }
                />
                <Route path="notifications" element={<Notifications />} />
                <Route
                  path="branding"
                  element={
                    <ProtectedRoute adminOnly>
                      <Branding />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="updates"
                  element={
                    <ProtectedRoute adminOnly>
                      <Updates />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="logs"
                  element={
                    <ProtectedRoute adminOnly>
                      <DebugLogs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="missing-metadata"
                  element={
                    <ProtectedRoute adminOnly>
                      <MissingMetadata />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="profile-cache"
                  element={
                    <ProtectedRoute adminOnly>
                      <ProfileCache />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="users"
                  element={
                    <ProtectedRoute adminOnly>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute adminOnly>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* Catch-all: native app wrappers → player, everyone else → admin login.
                  A stale "Web Player" opt-in flag no longer hijacks unknown URLs
                  on the public marketing domain. */}
              <Route
                path="*"
                element={<Navigate to={IS_NATIVE ? "/player" : "/admin/login"} replace />}
              />
            </Routes>
          </DeviceProvider>
        </AuthProvider>
      </Router>
      {/* Floating debug console — only renders when the user activated
          with the magic "logtest" key. Mounted at app root so it stays
          visible across every route, including the activation screen. */}
      <DebugConsole />
    </div>
  );
}

export default App;
